/**
 * Central admin-language resolution (PAM i18n workstream, contract C4).
 *
 * Precedence:
 *   explicit session selection
 *     > `pam_guest_language`   (deliberate pre-auth choice on the login screen)
 *     > `pam_admin_language`   (mirror of the /auth/me `language` profile field)
 *     > `localization.default_language` (client default)
 *     > `app_currency_config.platform_language` (pre-localization fallback)
 *     > `en`
 *
 * The guest key stays separate from the admin mirror so a deliberate pre-auth
 * pick is distinguishable from a mirrored profile value; on login the guest
 * choice wins and is PATCHed to the profile, but the guest key is kept so the
 * login screen still shows that language after logout.
 */

import { AppStrings } from "../constants/AppStrings";
import {
  DEFAULT_LANGUAGE,
  canonicalizeLanguageTag,
} from "./supportedLanguages";
import {
  applyI18nLanguage,
  normalizePlatformLanguageToI18nLng,
} from "./platformLanguage";

export const ADMIN_LANGUAGE_STORAGE_KEY = "pam_admin_language";
export const GUEST_LANGUAGE_STORAGE_KEY = "pam_guest_language";

// ============================================================================
// Explicit session selection (top of the precedence chain)
// ============================================================================

/**
 * A user changing the selector always wins for the current session. The
 * apply mechanism is a full reload, so this mostly matters for resolution
 * calls that happen between the selection and the reload.
 */
let sessionLanguage: string | null = null;

export const setSessionLanguage = (lng: string | null): void => {
  sessionLanguage = lng;
};

export const getSessionLanguage = (): string | null => sessionLanguage;

// ============================================================================
// localStorage keys (pam_admin_timezone pattern)
// ============================================================================

const getStoredValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

const setStoredValue = (key: string, value: string | null): void => {
  try {
    if (!value) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, value);
  } catch {
    // ignore localStorage errors
  }
};

/** Authenticated mirror of the /auth/me `language` profile preference. */
export const getStoredAdminLanguage = (): string | null =>
  getStoredValue(ADMIN_LANGUAGE_STORAGE_KEY);

export const setStoredAdminLanguage = (lng: string | null): void =>
  setStoredValue(ADMIN_LANGUAGE_STORAGE_KEY, lng);

/** Deliberate pre-auth choice made on the login screen. */
export const getStoredGuestLanguage = (): string | null =>
  getStoredValue(GUEST_LANGUAGE_STORAGE_KEY);

export const setStoredGuestLanguage = (lng: string | null): void =>
  setStoredValue(GUEST_LANGUAGE_STORAGE_KEY, lng);

// ============================================================================
// Resolution
// ============================================================================

type AppConfigLike = {
  app_currency_config?: { platform_language?: unknown };
  localization?: {
    default_language?: unknown;
    available_languages?: unknown;
  };
};

/**
 * Client default. `platform_language` stays as the fallback so configs
 * predating the `localization` block resolve exactly as before.
 */
export function resolvePlatformDefaultLanguage(
  appConfig?: AppConfigLike | null,
): string {
  return (
    canonicalizeLanguageTag(appConfig?.localization?.default_language) ??
    normalizePlatformLanguageToI18nLng(
      appConfig?.app_currency_config?.platform_language,
    )
  );
}

/**
 * Resolves the active language for the app.
 *
 * @param appConfig platform config payload (for the client default)
 * @param profileLanguage optional /auth/me `language` value; when omitted the
 *   `pam_admin_language` mirror stands in for it (they carry the same value —
 *   `useAdminAuth` keeps the mirror in sync).
 */
export function resolveActiveLanguage(
  appConfig?: AppConfigLike | null,
  profileLanguage?: string | null,
): string {
  const platformLanguage = resolvePlatformDefaultLanguage(appConfig);

  return (
    canonicalizeLanguageTag(sessionLanguage) ??
    canonicalizeLanguageTag(getStoredGuestLanguage()) ??
    canonicalizeLanguageTag(
      profileLanguage !== undefined ? profileLanguage : getStoredAdminLanguage(),
    ) ??
    platformLanguage
  );
}

/**
 * Boot-time resolution for src/index.js, before /auth/me or the platform
 * config are available: `pam_guest_language` > `pam_admin_language` > `en`.
 */
export function resolveBootLanguage(): string {
  return (
    canonicalizeLanguageTag(getStoredGuestLanguage()) ??
    canonicalizeLanguageTag(getStoredAdminLanguage()) ??
    DEFAULT_LANGUAGE
  );
}

/**
 * Single apply entry point for App.js and LogIn.js: resolves the active
 * language (preference/guest choice over the client default) and applies it
 * to i18next + AppStrings.
 */
export async function applyResolvedI18nLanguage(
  appConfig?: AppConfigLike | null,
): Promise<void> {
  await applyI18nLanguage(resolveActiveLanguage(appConfig));
}

// ============================================================================
// Display names (selector labels via AppStrings)
// ============================================================================

const LANGUAGE_NAME_KEYS: Record<string, string> = {
  en: "LANGUAGE_NAME_EN",
  es: "LANGUAGE_NAME_ES",
  de: "LANGUAGE_NAME_DE",
  "pt-BR": "LANGUAGE_NAME_PT_BR",
};

/**
 * Autonym for a tag with no curated label ("Nigerian English" for `en-NG`), so
 * onboarding a locale needs no new AppStrings key and TCM upload. Undefined when
 * the runtime cannot name the tag.
 */
function getLanguageAutonym(lng: string): string | undefined {
  try {
    return new Intl.DisplayNames([lng], { type: "language" }).of(lng);
  } catch {
    return undefined;
  }
}

/** Selector label: curated copy, then the autonym, then the raw tag. */
export function getLanguageDisplayName(lng: string): string {
  const key = LANGUAGE_NAME_KEYS[lng];
  return (key && AppStrings[key]) || getLanguageAutonym(lng) || lng;
}
